/**
 * Символ мебели из конструктора на плане и на развёртке.
 *
 * На плане шкаф рисуется символом платяного шкафа, модуль — контуром с лицевой кромкой
 * (навесной — пунктиром, как верхние шкафы на чертежах), кухня — своим чертежом из модулей
 * (`kitchenPlan`): корпуса по стенам, мойка, конфорки, колонны крестом, пунктир верхних шкафов.
 * Спереди (на развёртке) шкаф и модуль рисуются по своим деталям: фасады, ящики и ручки
 * там, где они есть на самом деле.
 */
import { FurnitureModuleType, KitchenModuleKind, WardrobeLayout } from "@/api";
import {
	KitchenPartMaterial,
	KitchenPartShape,
	kitchenPieces,
	kitchenPlan,
	kitchenPlanUppers,
	kitchenSize,
	kitchenWorktops,
	moduleParts,
	splitKitchenPieceId,
	useCustomFurnitureStore,
	WardrobePartMaterial,
	WardrobePartShape,
	wardrobeParts,
	wardrobePlan,
	wardrobeSize,
	type KitchenPart,
	type WardrobeDesign,
	type WardrobePart,
} from "@/shared/catalog";
import { UIKit, type SymbolProps } from "@/shared/ui";
import { FURNITURE_SYMBOL_BY_ID } from "../../furniture/2d/symbols";

const line = { fill: "none", stroke: "currentColor" } as const;

/** Прямоугольник по центру модуля: вдоль стены и от стены, мм */
const rectAt = (
	center: readonly [number, number],
	along: readonly [number, number],
	alongSize: number,
	intoSize: number,
) => {
	const horizontal = Math.abs(along[0]) > 0.5;
	const w = horizontal ? alongSize : intoSize;
	const h = horizontal ? intoSize : alongSize;
	return { x: center[0] - w / 2, y: center[1] - h / 2, width: w, height: h };
};

const KitchenSymbol = ({ kitchenId, ...props }: SymbolProps & { kitchenId: string }) => {
	const { kitchenId: id, run } = splitKitchenPieceId(kitchenId);
	const kitchen = useCustomFurnitureStore((s) => s.kitchens.find((k) => k.id === id));
	if (!kitchen) return null;
	// Часть кухни рисуется в своём габарите со своим центром, вся кухня — в общем
	const piece = run ? kitchenPieces(kitchen).find((p) => p.run === run) : undefined;
	const whole = kitchenSize(kitchen);
	const width = piece?.width ?? whole.width;
	const depth = piece?.depth ?? whole.depth;
	const [cx, cz] = piece?.center ?? [0, 0];
	// Чертёж в мм от центра кухни, viewBox — от левого верхнего угла
	const p = (pt: readonly [number, number]) =>
		`${pt[0] - cx + width / 2},${pt[1] - cz + depth / 2}`;
	const shift = (pt: readonly [number, number]) =>
		[pt[0] - cx + width / 2, pt[1] - cz + depth / 2] as const;
	return (
		<UIKit.SymbolFrame viewBox={`0 0 ${width} ${depth}`} {...props}>
			{/* Столешница — под модулями: её контур со свесом и есть габарит кухни на плане */}
			{kitchenWorktops(kitchen)
				.filter((top) => !run || top.run === run)
				.map((top, index) => (
					<polygon
						key={`top-${index}`}
						points={top.outline.map(p).join(" ")}
						{...line}
						strokeWidth="8"
					/>
				))}
			{kitchenPlan(kitchen)
				.filter((module) => !run || module.run === run)
				.map((module, index) => {
					const [mx, my] = shift(module.center);
					const center = [mx, my] as const;
					const key = `${index}-${module.kind}`;
					const [a, , c] = module.outline.map(shift);
					return (
						<g key={key}>
							<polygon points={module.outline.map(p).join(" ")} {...line} strokeWidth="12" />
							{(module.kind === KitchenModuleKind.Fridge ||
								module.kind === KitchenModuleKind.Column) && (
								<path
									d={`M${a[0]} ${a[1]}L${c[0]} ${c[1]}M${module.outline.map(shift)[1].join(" ")}L${module.outline.map(shift)[3].join(" ")}`}
									stroke="currentColor"
									strokeWidth="6"
									opacity="0.55"
								/>
							)}
							{module.kind === KitchenModuleKind.Sink && (
								<>
									<rect
										{...rectAt(center, module.along, Math.min(500, module.width - 120), 420)}
										rx="40"
										{...line}
										strokeWidth="9"
									/>
									<circle cx={mx} cy={my} r="22" {...line} strokeWidth="6" />
								</>
							)}
							{(module.kind === KitchenModuleKind.Cooktop ||
								module.kind === KitchenModuleKind.Oven) &&
								[
									[-140, -120, 85],
									[140, -120, 65],
									[-140, 120, 65],
									[140, 120, 85],
								].map(([da, di, r]) => (
									<circle
										key={`${da}${di}`}
										cx={mx + module.along[0] * da + module.into[0] * di}
										cy={my + module.along[1] * da + module.into[1] * di}
										r={r}
										{...line}
										strokeWidth="8"
									/>
								))}
							{module.kind === KitchenModuleKind.Bottle && (
								<rect
									{...rectAt(center, module.along, Math.max(40, module.width - 80), 420)}
									{...line}
									strokeWidth="5"
								/>
							)}
							{module.kind === KitchenModuleKind.Dishwasher && (
								<rect
									{...rectAt(center, module.along, module.width - 140, 420)}
									{...line}
									strokeWidth="5"
									strokeDasharray="20 16"
								/>
							)}
						</g>
					);
				})}
			{/* Верхние шкафы — пунктиром по их переду */}
			{kitchenPlanUppers(kitchen)
				.filter((upper) => !run || upper.run === run)
				.map((upper, index) => (
					<path
						key={`upper-${index}`}
						d={`M${shift(upper.line[0]).join(" ")}L${shift(upper.line[1]).join(" ")}`}
						stroke="currentColor"
						strokeWidth="7"
						strokeDasharray="40 24"
					/>
				))}
		</UIKit.SymbolFrame>
	);
};

/** Деталь вида спереди: центр, ширина и высота на фасаде, глубина передней кромки — м */
interface FrontRect {
	x: number;
	y: number;
	w: number;
	h: number;
	front: number;
	kind: "handle" | "front" | "body";
}

const kitchenFrontRect = (part: KitchenPart): FrontRect | null => {
	// Короба ящиков спрятаны за фасадом — на виде спереди их не рисуем
	if (part.motion && part.material === KitchenPartMaterial.Body) return null;
	const cylinder = part.shape === KitchenPartShape.Cylinder;
	return {
		x: part.position.x,
		y: part.position.y,
		w: cylinder ? part.size.x * 2 : part.size.x,
		h: part.size.y,
		front: part.position.z + (cylinder ? part.size.x : part.size.z / 2),
		kind:
			part.material === KitchenPartMaterial.Handle || part.material === KitchenPartMaterial.Steel
				? "handle"
				: part.material === KitchenPartMaterial.Front
					? "front"
					: "body",
	};
};

const wardrobeFrontRect = (part: WardrobePart): FrontRect => {
	// Штанга лежит вдоль X: длина и радиус
	const rod = part.shape === WardrobePartShape.Rod;
	return {
		x: part.position[0],
		y: part.position[1],
		w: part.size[0],
		h: rod ? part.size[1] * 2 : part.size[1],
		front: part.position[2] + part.size[2] / 2,
		kind:
			part.material === WardrobePartMaterial.Handle || part.material === WardrobePartMaterial.Metal
				? "handle"
				: part.material === WardrobePartMaterial.Door
					? "front"
					: "body",
	};
};

/**
 * Вид спереди по деталям: брусок — прямоугольник по X и высоте, задние — первыми,
 * фасады и ручки — поверх. Детали в метрах от центра, рисунок — в мм от верхнего левого угла.
 */
const PartsFront = ({ rects, width, height, ...props }: SymbolProps & { rects: FrontRect[] }) => {
	const W = width;
	const H = height;
	const sorted = rects.toSorted((a, b) => a.front - b.front);
	return (
		<UIKit.SymbolFrame viewBox={`0 0 ${W} ${H}`} width={width} height={height} {...props}>
			{sorted.map((rect, index) => {
				const isHandle = rect.kind === "handle";
				return (
					<rect
						key={`${index}-${rect.kind}`}
						x={(rect.x - rect.w / 2) * 1000 + W / 2}
						y={H / 2 - (rect.y + rect.h / 2) * 1000}
						width={rect.w * 1000}
						height={rect.h * 1000}
						fill={isHandle ? "currentColor" : "none"}
						stroke="currentColor"
						strokeWidth={isHandle ? 2 : rect.kind === "front" ? 6 : 4}
					/>
				);
			})}
		</UIKit.SymbolFrame>
	);
};

/** Модуль сверху: контур и лицевая кромка; навесной — пунктиром */
const ModulePlan = ({ wall, ...props }: SymbolProps & { wall: boolean }) => (
	<UIKit.SymbolFrame viewBox={`0 0 ${props.width} ${props.height}`} {...props}>
		<rect
			x="8"
			y="8"
			width={props.width - 16}
			height={props.height - 16}
			{...line}
			strokeWidth="12"
			strokeDasharray={wall ? "50 30" : undefined}
		/>
		<path
			d={`M8 ${props.height - 30}H${props.width - 8}`}
			stroke="currentColor"
			strokeWidth="8"
			strokeDasharray={wall ? "50 30" : undefined}
		/>
	</UIKit.SymbolFrame>
);

const WardrobeSymbol = FURNITURE_SYMBOL_BY_ID.get("wardrobe");

/** Угловой или П-образный шкаф сверху: контуры крыльев и лицевые кромки */
const WardrobePlanSymbol = ({ wardrobe, ...props }: SymbolProps & { wardrobe: WardrobeDesign }) => {
	const { width, depth } = wardrobeSize(wardrobe);
	const shift = (pt: readonly [number, number]) => [pt[0] + width / 2, pt[1] + depth / 2] as const;
	const p = (pt: readonly [number, number]) => shift(pt).join(",");
	return (
		<UIKit.SymbolFrame viewBox={`0 0 ${width} ${depth}`} {...props}>
			{wardrobePlan(wardrobe).map((run) => {
				const [a, b, c, d] = run.outline.map(shift);
				return (
					<g key={run.run}>
						<polygon points={run.outline.map(p).join(" ")} {...line} strokeWidth="14" />
						<path
							d={`M${a[0]} ${a[1]}L${c[0]} ${c[1]}M${b[0]} ${b[1]}L${d[0]} ${d[1]}`}
							stroke="currentColor"
							strokeWidth="6"
							opacity="0.45"
						/>
						<path
							d={`M${shift(run.frontLine[0]).join(" ")}L${shift(run.frontLine[1]).join(" ")}`}
							stroke="currentColor"
							strokeWidth="9"
						/>
					</g>
				);
			})}
		</UIKit.SymbolFrame>
	);
};

/** Есть ли у мебели из конструктора свой вид спереди — у шкафа и модуля да, у кухни пока нет */
export const hasCustomFrontView = (itemId: string) => {
	const { designs, modules } = useCustomFurnitureStore.getState();
	return designs.some((d) => d.id === itemId) || modules.some((m) => m.id === itemId);
};

export const CustomFurnitureSymbol = ({ itemId, ...props }: SymbolProps & { itemId: string }) => {
	const front = props.facing !== undefined && props.facing !== "plan";
	const module = useCustomFurnitureStore((s) => s.modules.find((m) => m.id === itemId));
	const wardrobe = useCustomFurnitureStore((s) => s.designs.find((d) => d.id === itemId));
	const isKitchen = useCustomFurnitureStore((s) =>
		s.kitchens.some((k) => k.id === splitKitchenPieceId(itemId).kitchenId),
	);
	if (module) {
		return front ? (
			<PartsFront
				rects={moduleParts(module).flatMap((part) => kitchenFrontRect(part) ?? [])}
				{...props}
			/>
		) : (
			<ModulePlan wall={module.type === FurnitureModuleType.Wall} {...props} />
		);
	}
	if (wardrobe) {
		if (front) {
			return <PartsFront rects={wardrobeParts(wardrobe).map(wardrobeFrontRect)} {...props} />;
		}
		if (wardrobe.layout !== undefined && wardrobe.layout !== WardrobeLayout.Straight) {
			return <WardrobePlanSymbol wardrobe={wardrobe} {...props} />;
		}
	}

	if (isKitchen) {
		// Спереди (на развёртке) кухня пока — контур габарита; вид фасадов — следующий шаг
		if (props.facing && props.facing !== "plan") {
			return (
				<UIKit.SymbolFrame viewBox="0 0 100 100" {...props}>
					<rect x="1" y="1" width="98" height="98" {...line} strokeWidth="1.5" />
				</UIKit.SymbolFrame>
			);
		}
		return <KitchenSymbol kitchenId={itemId} {...props} />;
	}
	return WardrobeSymbol ? <WardrobeSymbol {...props} /> : null;
};
